import { 
  WorkloadConfig, 
  ConfigSchema, 
  DeploymentStatus, 
  WorkloadMetrics,
  components,
  paths
} from '../types';
import { MockApiService } from './mock-api';
import { LocalFsApiService } from './local-fs-api';
import { fetchAuthSession, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';


const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const CERTIFICATE_API_BASE_URL = import.meta.env.VITE_CERTIFICATE_API_URL || import.meta.env.VITE_API_URL || '/api';
const MTLS_API_BASE_URL = import.meta.env.VITE_MTLS_API_URL || import.meta.env.VITE_API_URL || '/api';
const IAM_M2M_API_BASE_URL = import.meta.env.VITE_IAM_M2M_API_URL || import.meta.env.VITE_API_URL || '/api';
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const USE_LOCAL_FS = import.meta.env.VITE_USE_LOCAL_FS === 'true';

import '../aws-config'; // Initialize Amplify configuration

console.log('API Service Configuration:', { USE_MOCK, USE_LOCAL_FS, API_BASE_URL, CERTIFICATE_API_BASE_URL, MTLS_API_BASE_URL, IAM_M2M_API_BASE_URL });



class RealApiService {
  static async getCurrentUser(): Promise<{ username: string; email: string } | null> {
    try {
      const user = await getCurrentUser();
      return {
        username: user.username,
        email: user.signInDetails?.loginId || 'unknown@example.com'
      };
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  static async signOut(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  static async signIn(username: string, password: string): Promise<void> {
    try {
      await signIn({ username, password });
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  private static async fetchWithAuth(url: string, options: RequestInit = {}) {
    try {
      // Get the current session with tokens
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('Not authenticated - no access token available');
      }

      // Get client_id from environment (for WAF rate limiting)
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'platform-ui';

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'client_id': clientId,
          ...options.headers,
        },
      });

      if (!response.ok) {
        // Try to parse error details from response body
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If parsing fails, throw generic error
          throw new Error(`API Error: ${response.statusText}`);
        }
        // Throw the error data so it can be caught with details
        throw errorData;
      }

      // Handle responses with no content (204, etc.)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      // For non-JSON responses, return text
      return response.text();
    } catch (error) {
      // If it's already an error object with API error details, re-throw it
      if (error && typeof error === 'object' && 'error' in error) {
        throw error;
      }
      
      // Only treat as auth error if it's actually an auth-related issue
      console.error('Authentication error:', error);
      throw new Error('Authentication failed - please sign in again');
    }
  }

  private static async fetchWithAuthCertificate(url: string, options: RequestInit = {}) {
    try {
      // Get the current session with tokens
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('Not authenticated - no access token available');
      }

      // Get client_id from environment (for WAF rate limiting)
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'platform-ui';

      const response = await fetch(`${CERTIFICATE_API_BASE_URL}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'client_id': clientId,
          ...options.headers,
        },
      });

      if (!response.ok) {
        // Try to parse error details from response body
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If parsing fails, throw generic error
          throw new Error(`Certificate API Error: ${response.statusText}`);
        }
        // Throw the error data so it can be caught with details
        throw errorData;
      }

      // Handle responses with no content (204, etc.)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      // For non-JSON responses, return text
      return response.text();
    } catch (error) {
      // If it's already an error object with API error details, re-throw it
      if (error && typeof error === 'object' && 'error' in error) {
        throw error;
      }
      
      // Only treat as auth error if it's actually an auth-related issue
      console.error('Certificate API authentication error:', error);
      throw new Error('Certificate API authentication failed - please sign in again');
    }
  }

  private static async fetchWithAuthMtls(url: string, options: RequestInit = {}) {
    try {
      // Get the current session with tokens
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('Not authenticated - no access token available');
      }

      // Get client_id from environment (for WAF rate limiting)
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'platform-ui';

      const response = await fetch(`${MTLS_API_BASE_URL}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'client_id': clientId,
          ...options.headers,
        },
      });

      if (!response.ok) {
        // Try to parse error details from response body
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If parsing fails, throw generic error
          throw new Error(`mTLS API Error: ${response.statusText}`);
        }
        // Throw the error data so it can be caught with details
        throw errorData;
      }

      // Handle responses with no content (204, etc.)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      // For non-JSON responses, return text
      return response.text();
    } catch (error) {
      // If it's already an error object with API error details, re-throw it
      if (error && typeof error === 'object' && ('error' in error || 'message' in error || 'validationErrors' in error)) {
        throw error;
      }
      
      // Only treat as auth error if it's actually an auth-related issue
      console.error('mTLS API authentication error:', error);
      throw new Error('mTLS API authentication failed - please sign in again');
    }
  }

  private static async fetchWithAuthIamM2m(url: string, options: RequestInit = {}) {
    try {
      // Get the current session with tokens
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('Not authenticated - no access token available');
      }

      // Get client_id from environment (for WAF rate limiting)
      const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'platform-ui';

      const response = await fetch(`${IAM_M2M_API_BASE_URL}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'client_id': clientId,
          ...options.headers,
        },
      });

      if (!response.ok) {
        // Try to parse error details from response body
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If parsing fails, throw generic error
          throw new Error(`IAM M2M API Error: ${response.statusText}`);
        }
        // Throw the error data so it can be caught with details
        throw errorData;
      }

      // Handle responses with no content (204, etc.)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      // For non-JSON responses, return text
      return response.text();
    } catch (error) {
      // If it's already an error object with API error details, re-throw it
      if (error && typeof error === 'object' && 'error' in error) {
        throw error;
      }
      
      // Only treat as auth error if it's actually an auth-related issue
      console.error('IAM M2M API authentication error:', error);
      throw new Error('IAM M2M API authentication failed - please sign in again');
    }
  }

  static async getSchema(): Promise<ConfigSchema> {
    return this.fetchWithAuth('/api/schema');
  }

  static async getInvoiceUnits(): Promise<Array<{name: string, arn: string, description: string}>> {
    try {
      const response = await this.fetchWithAuth('/api/invoice-units');
      return response.invoiceUnits || [];
    } catch (error) {
      console.error('Failed to fetch invoice units:', error);
      return [];
    }
  }

  static async getOrganizationalUnits(): Promise<string[]> {
    try {
      const response = await this.fetchWithAuth('/api/organizational-units');
      return response.organizationalUnits || [];
    } catch (error) {
      console.error('Failed to fetch organizational units:', error);
      return [];
    }
  }

  static async validateAccount(accountName?: string, accountEmail?: string): Promise<{
    valid: boolean;
    errors: { accountName?: string; accountEmail?: string };
  }> {
    try {
      const response = await this.fetchWithAuth('/api/validate-account', {
        method: 'POST',
        body: JSON.stringify({ accountName, accountEmail }),
      });
      return response;
    } catch (error) {
      console.error('Failed to validate account:', error);
      return { valid: false, errors: { accountName: 'Validation failed', accountEmail: 'Validation failed' } };
    }
  }

  static async listConfigs(): Promise<string[]> {
    const response = await this.fetchWithAuth('/api/workloads');
    return response.workloads || [];
  }

  static async getConfig(workloadName: string): Promise<WorkloadConfig> {
    const response = await this.fetchWithAuth(`/api/workloads/${workloadName}`);
    const config = response.config || response;
    
    // Ensure workloadName is in the config (in case it's missing from the file)
    return {
      workloadName: config.workloadName || workloadName,
      ...config
    };
  }

  static async createConfig(config: WorkloadConfig): Promise<any> {
    // Extract workloadName and remove UI-specific fields
    const { workloadName, gitlabRepository, ...cleanConfig } = config as any;
    
    return this.fetchWithAuth('/api/workloads', {
      method: 'POST',
      body: JSON.stringify({
        name: workloadName,
        config: cleanConfig,
      }),
    });
  }

  static async updateConfig(workloadName: string, config: WorkloadConfig): Promise<any> {
    // Remove UI-specific fields
    const { gitlabRepository, ...cleanConfig } = config as any;
    
    return this.fetchWithAuth(`/api/workloads/${workloadName}`, {
      method: 'PUT',
      body: JSON.stringify({ config: cleanConfig }),
    });
  }

  static async deleteConfig(workloadName: string): Promise<void> {
    await this.fetchWithAuth(`/api/workloads/${workloadName}`, {
      method: 'DELETE',
    });
  }

  static async getTemplates(): Promise<Record<string, WorkloadConfig>> {
    return this.fetchWithAuth('/api/templates');
  }

  // Reporting API
  static async getDeploymentStatus(): Promise<DeploymentStatus> {
    return this.fetchWithAuth('/api/reporting/deployment-status');
  }

  static async getWorkloadMetrics(workloadName: string): Promise<WorkloadMetrics> {
    return this.fetchWithAuth(`/api/reporting/workloads/${workloadName}/metrics`);
  }

  // PAT User Management API
  static async listPATUsers(filter?: string, page?: number): Promise<paths['/api/pat/cognito/users']['get']['responses']['200']['content']['application/json']> {
    const params = new URLSearchParams();
    if (filter) params.append('filter', filter);
    if (page) params.append('page', page.toString());
    
    const queryString = params.toString();
    const url = `/api/pat/cognito/users${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuth(url);
  }

  static async getPATUser(username: string): Promise<components['schemas']['PATUser']> {
    return this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}`);
  }

  static async createPATUser(request: paths['/api/pat/cognito/users']['post']['requestBody']['content']['application/json']): Promise<components['schemas']['PATUser']> {
    return this.fetchWithAuth('/api/pat/cognito/users', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async updatePATUser(username: string, request: paths['/api/pat/cognito/users/{username}']['put']['requestBody']['content']['application/json']): Promise<components['schemas']['PATUser']> {
    return this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  static async disablePATUser(username: string): Promise<void> {
    await this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}/disable`, {
      method: 'POST',
    });
  }

  static async enablePATUser(username: string): Promise<void> {
    await this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}/enable`, {
      method: 'POST',
    });
  }

  static async deletePATUser(username: string): Promise<void> {
    await this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    });
  }

  // PAT User Group Assignment API
  static async listPATUserGroups(username: string): Promise<any> {
    return this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}/groups`);
  }

  static async addPATUserToGroup(username: string, groupName: string): Promise<void> {
    await this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(groupName)}`, {
      method: 'POST',
    });
  }

  static async removePATUserFromGroup(username: string, groupName: string): Promise<void> {
    await this.fetchWithAuth(`/api/pat/cognito/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(groupName)}`, {
      method: 'DELETE',
    });
  }

  // PAT Group Management API
  static async listPATGroups(): Promise<any> {
    return this.fetchWithAuth('/api/pat/cognito/groups');
  }

  static async createPATGroup(request: paths['/api/pat/cognito/groups']['post']['requestBody']['content']['application/json']): Promise<any> {
    return this.fetchWithAuth('/api/pat/cognito/groups', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async deletePATGroup(groupName: string): Promise<void> {
    await this.fetchWithAuth(`/api/pat/cognito/groups/${encodeURIComponent(groupName)}`, {
      method: 'DELETE',
    });
  }

  // PAT App Client Management API - TODO: Add to OpenAPI spec
  // static async listPATAppClients(): Promise<any> {
  //   return this.fetchWithAuth('/api/pat/cognito/app-clients');
  // }

  // static async createPATAppClient(request: any): Promise<any> {
  //   return this.fetchWithAuth('/api/pat/cognito/app-clients', {
  //     method: 'POST',
  //     body: JSON.stringify(request),
  //   });
  // }

  // Certificate Management API - Using OpenAPI generated types
  static async listCertificates(
    status?: 'active' | 'revoked' | 'expired', 
    environment?: 'prod' | 'non-prod', 
    limit?: number, 
    nextToken?: string
  ): Promise<paths['/api/certificates']['get']['responses']['200']['content']['application/json']> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (environment) params.append('environment', environment);
    if (limit) params.append('limit', limit.toString());
    if (nextToken) params.append('nextToken', nextToken);
    
    const queryString = params.toString();
    const url = `/api/certificates${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthCertificate(url);
  }

  static async getCertificate(certificateId: string): Promise<paths['/api/certificates/{id}']['get']['responses']['200']['content']['application/json']> {
    return this.fetchWithAuthCertificate(`/api/certificates/${encodeURIComponent(certificateId)}`);
  }

  static async createCertificate(
    request: paths['/api/certificates']['post']['requestBody']['content']['application/json']
  ): Promise<paths['/api/certificates']['post']['responses']['201']['content']['application/json']> {
    return this.fetchWithAuthCertificate('/api/certificates', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async revokeCertificate(
    certificateId: string, 
    request: paths['/api/certificates/{id}']['delete']['requestBody']['content']['application/json']
  ): Promise<void> {
    await this.fetchWithAuthCertificate(`/api/certificates/${encodeURIComponent(certificateId)}`, {
      method: 'DELETE',
      body: JSON.stringify(request),
    });
  }

  static async downloadCertificate(certificateId: string): Promise<paths['/api/certificates/{id}/download']['get']['responses']['200']['content']['application/json']> {
    return this.fetchWithAuthCertificate(`/api/certificates/${encodeURIComponent(certificateId)}/download`);
  }

  static async getCAInfo(environment?: 'prod' | 'non-prod'): Promise<paths['/api/ca/info']['get']['responses']['200']['content']['application/json']> {
    const params = new URLSearchParams();
    if (environment) params.append('environment', environment);
    
    const queryString = params.toString();
    const url = `/api/ca/info${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthCertificate(url);
  }

  static async getCAHealth(environment?: 'prod' | 'non-prod'): Promise<paths['/api/ca/health']['get']['responses']['200']['content']['application/json']> {
    const params = new URLSearchParams();
    if (environment) params.append('environment', environment);
    
    const queryString = params.toString();
    const url = `/api/ca/health${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthCertificate(url);
  }

  // mTLS Organization Management API - Using OpenAPI generated types
  static async listMtlsOrganizations(
    page?: number, 
    limit?: number, 
    name?: string,
    email?: string,
    hasProducts?: boolean,
    hasCertificates?: boolean
  ): Promise<paths['/api/mtls/organizations']['get']['responses']['200']['content']['application/json']> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (name) params.append('name', name);
    if (email) params.append('email', email);
    if (hasProducts !== undefined) params.append('hasProducts', hasProducts.toString());
    if (hasCertificates !== undefined) params.append('hasCertificates', hasCertificates.toString());
    
    const queryString = params.toString();
    const url = `/api/mtls/organizations${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  static async createMtlsOrganization(
    request: paths['/api/mtls/organizations']['post']['requestBody']['content']['application/json']
  ): Promise<paths['/api/mtls/organizations']['post']['responses']['201']['content']['application/json']> {
    return this.fetchWithAuthMtls('/api/mtls/organizations', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async getMtlsOrganization(organizationId: number): Promise<paths['/api/mtls/organizations/{id}']['get']['responses']['200']['content']['application/json']> {
    return this.fetchWithAuthMtls(`/api/mtls/organizations/${organizationId}`);
  }

  static async updateMtlsOrganization(
    organizationId: number, 
    request: paths['/api/mtls/organizations/{id}']['put']['requestBody']['content']['application/json']
  ): Promise<paths['/api/mtls/organizations/{id}']['put']['responses']['200']['content']['application/json']> {
    return this.fetchWithAuthMtls(`/api/mtls/organizations/${organizationId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  static async deleteMtlsOrganization(organizationId: number): Promise<void> {
    await this.fetchWithAuthMtls(`/api/mtls/organizations/${organizationId}`, {
      method: 'DELETE',
    });
  }

  // Organization Products API - Using OpenAPI generated types
  static async listMtlsOrganizationProducts(
    organizationId: number,
    page?: number,
    limit?: number
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page.toString());
    if (limit) queryParams.append('limit', limit.toString());
    const queryString = queryParams.toString();
    const url = `/api/mtls/organizations/${organizationId}/products${queryString ? `?${queryString}` : ''}`;
    return this.fetchWithAuthMtls(url);
  }

  static async linkProductToOrganization(organizationId: number, productId: number): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/organizations/${organizationId}/products`, {
      method: 'POST',
      body: JSON.stringify({ productId }),
    });
  }

  static async unlinkProductFromOrganization(organizationId: number, productId: number): Promise<void> {
    await this.fetchWithAuthMtls(`/api/mtls/organizations/${organizationId}/products/${productId}`, {
      method: 'DELETE',
    });
  }

  static async listMtlsProductOrganizations(productId: number, page?: number, limit?: number): Promise<any> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return this.fetchWithAuthMtls(`/api/mtls/products/${productId}/organizations${queryString ? `?${queryString}` : ''}`);
  }



  // mTLS Product Management API - Using OpenAPI generated types
  static async listMtlsProducts(
    page?: number,
    limit?: number,
    name?: string,
    organizationId?: number
  ): Promise<paths['/api/mtls/products']['get']['responses']['200']['content']['application/json']> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (name) params.append('name', name);
    if (organizationId) params.append('organizationId', organizationId.toString());
    
    const queryString = params.toString();
    const url = `/api/mtls/products${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  static async getMtlsProduct(productId: number): Promise<paths['/api/mtls/products/{id}']['get']['responses']['200']['content']['application/json']> {
    return this.fetchWithAuthMtls(`/api/mtls/products/${productId}`);
  }

  static async createMtlsProduct(
    request: paths['/api/mtls/products']['post']['requestBody']['content']['application/json']
  ): Promise<paths['/api/mtls/products']['post']['responses']['201']['content']['application/json']> {
    return this.fetchWithAuthMtls('/api/mtls/products', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async updateMtlsProduct(
    productId: number,
    request: paths['/api/mtls/products/{id}']['put']['requestBody']['content']['application/json']
  ): Promise<paths['/api/mtls/products/{id}']['put']['responses']['200']['content']['application/json']> {
    return this.fetchWithAuthMtls(`/api/mtls/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  static async deleteMtlsProduct(productId: number): Promise<void> {
    await this.fetchWithAuthMtls(`/api/mtls/products/${productId}`, {
      method: 'DELETE',
    });
  }

  // mTLS Environment Management API
  static async listMtlsEnvironments(
    productId: number,
    page?: number,
    limit?: number
  ): Promise<any> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    const url = `/api/mtls/products/${productId}/environments${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  static async createMtlsEnvironment(request: any): Promise<any> {
    const productId = request.productId;
    return this.fetchWithAuthMtls(`/api/mtls/products/${productId}/environments`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async updateMtlsEnvironment(environmentId: number, request: any): Promise<any> {
    const productId = request.productId;
    return this.fetchWithAuthMtls(`/api/mtls/products/${productId}/environments/${environmentId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  static async deleteMtlsEnvironment(environmentId: number, productId: number): Promise<void> {
    await this.fetchWithAuthMtls(`/api/mtls/products/${productId}/environments/${environmentId}`, {
      method: 'DELETE',
    });
  }

  // mTLS Certificate Management API
  // Organization Certificates API - Using OpenAPI generated types
  static async listMtlsOrganizationCertificates(
    organizationId: number,
    page?: number,
    limit?: number
  ): Promise<any> { // TODO: Replace with paths['/api/mtls/organizations/{id}/certificates']['get']['responses']['200']['content']['application/json'] when types are generated
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page.toString());
    if (limit) queryParams.append('limit', limit.toString());
    
    const queryString = queryParams.toString();
    const url = `/api/mtls/organizations/${organizationId}/certificates${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }



  static async createMtlsCertificate(request: {
    organizationId: number;
    certificateType: "prod" | "non-prod";
    certificateData: string;
    description?: string;
  }): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/certificates', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async uploadMtlsCertificate(
    organizationId: number, 
    file: File, 
    description?: string, 
    certificateType: "prod" | "non-prod" = "non-prod"
  ): Promise<any> {
    // Read file content
    const fileContent = await file.text();
    
    return this.createMtlsCertificate({
      organizationId,
      certificateType,
      certificateData: fileContent,
      description,
    });
  }

  // Deploy trust store to environment (what the backend endpoint actually does)
  static async deployTrustStoreToEnvironment(productId: number, environmentId: number): Promise<any> {
    // Get auth token
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'platform-ui';

    const response = await fetch(`${MTLS_API_BASE_URL}/api/product/${productId}/environment/${environmentId}/uploadCertificates`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'client_id': clientId,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    return response.json();
  }

  static async downloadMtlsCertificate(certificateId: number): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/certificates/${certificateId}/download`);
  }

  static async deleteMtlsCertificate(certificateId: number): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/certificates/${certificateId}`, {
      method: 'DELETE',
    });
  }

  static async deployMtlsCertificate(certificateId: number): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/certificates/${certificateId}/deploy`, {
      method: 'POST',
    });
  }

  static async getTrustStoreStatus(trustStoreId: string): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/trust-stores/${trustStoreId}/status`);
  }

  // Real-time status endpoints
  

  

  // Enhanced download endpoints with streaming support
  static async downloadMtlsCertificateStream(certificateId: number): Promise<Response> {
    const token = localStorage.getItem('authToken');
    return fetch(`/api/mtls/certificates/${certificateId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  static async downloadTrustStoreStream(environmentId: number): Promise<Response> {
    const token = localStorage.getItem('authToken');
    return fetch(`/api/mtls/trust-stores/${environmentId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  // mTLS Certificate Assignment API
  static async listMtlsAssignments(
    certificateId?: number,
    environmentId?: number
  ): Promise<any> {
    const params = new URLSearchParams();
    if (certificateId) params.append('certificateId', certificateId.toString());
    if (environmentId) params.append('environmentId', environmentId.toString());
    
    const queryString = params.toString();
    const url = `/api/mtls/assignments${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  static async validateCertificateAssignment(certificateId: string, environmentIds: string[]): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/certificates/${certificateId}/validate-assignment`, {
      method: 'POST',
      body: JSON.stringify({ environmentIds }),
    });
  }

  static async assignMtlsCertificate(certificateId: number, environmentId: number): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/assignments', {
      method: 'POST',
      body: JSON.stringify({ certificateId, environmentId }),
    });
  }

  static async unassignMtlsCertificate(certificateId: number, environmentId: number): Promise<void> {
    await this.fetchWithAuthMtls(`/api/mtls/assignments/${certificateId}/${environmentId}`, {
      method: 'DELETE',
    });
  }

  // Helper methods for assignment management
  static async getAvailableCertificatesForAssignment(): Promise<any> {
    const response = await this.fetchWithAuthMtls('/api/mtls/certificates?status=active&limit=100');
    // API returns paginated { data: [...] } shape
    const certs = response.data || response.certificates || [];
    return { certificates: certs };
  }

  static async getAvailableEnvironmentsForAssignment(): Promise<any> {
    // Derive from assignments - include all statuses so removed assignments still show the env
    const response = await this.fetchWithAuthMtls('/api/mtls/assignments?limit=100');
    const envs = (response.data || []).map((a: any) => ({
      id: a.productEnvironmentId ?? a.environmentId,
      environmentName: a.environmentName,
      productName: a.productName,
      environmentType: a.environmentType,
    }));
    const unique = Array.from(new Map(envs.map((e: any) => [e.id, e])).values());
    return { environments: unique };
  }

  static async getAssignmentMatrix(): Promise<any> {
    // matrix endpoint not implemented - return assignments as flat list
    return this.fetchWithAuthMtls('/api/mtls/assignments?limit=100');
  }

  static async getPendingChanges(environmentId?: number): Promise<any> {
    const params = environmentId ? `?environmentId=${environmentId}` : '';
    return this.fetchWithAuthMtls(`/api/mtls/assignments/pending-changes${params}`);
  }

  static async getAssignmentAudit(filters?: { certificateId?: number; environmentId?: number; action?: string; from?: string; to?: string; page?: number; limit?: number }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.certificateId) params.append('certificateId', filters.certificateId.toString());
    if (filters?.environmentId) params.append('environmentId', filters.environmentId.toString());
    if (filters?.action) params.append('action', filters.action);
    if (filters?.from) params.append('from', filters.from);
    if (filters?.to) params.append('to', filters.to);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const qs = params.toString();
    return this.fetchWithAuthMtls(`/api/mtls/assignments/audit${qs ? `?${qs}` : ''}`);
  }

  // mTLS Deployment Status API
  static async getMtlsDeploymentStatus(): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/deployments/status');
  }

  static async syncMtlsDeployment(environmentId: number): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/deployments/sync/${environmentId}`, {
      method: 'POST',
    });
  }

  // mTLS Dashboard Statistics API
  static async getMtlsDashboardStats(): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/dashboard/stats');
  }

  // mTLS Recent Activity API
  static async getMtlsRecentActivity(limit?: number): Promise<any> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    const url = `/api/mtls/dashboard/activity${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  // mTLS Expiring Certificates API
  static async getMtlsExpiringCertificates(days?: number): Promise<any> {
    const params = new URLSearchParams();
    if (days) params.append('days', days.toString());
    
    const queryString = params.toString();
    const url = `/api/mtls/certificates/expiring${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  // mTLS Trust Store Sync API
  static async syncAllTrustStores(): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/trust-stores/sync-all', {
      method: 'POST',
    });
  }

  // mTLS Audit Trail API (removed duplicate - using enhanced version below)

  // mTLS Reporting API (removed duplicate - using enhanced version below)

  static async getMtlsDeploymentReport(
    environmentId?: number,
    startDate?: string,
    endDate?: string,
    page?: number,
    size?: number,
    search?: string
  ): Promise<any> {
    const params = new URLSearchParams();
    if (environmentId) params.append('environmentId', environmentId.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (page) params.append('page', page.toString());
    if (size) params.append('size', size.toString());
    if (search) params.append('search', search);
    
    const queryString = params.toString();
    const url = `/api/mtls/reports/deployments${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  // Enhanced mTLS Audit Trail API
  static async getMtlsAuditTrail(
    resourceType?: string,
    resourceId?: string,
    startDate?: string,
    endDate?: string,
    page?: number,
    size?: number,
    search?: string,
    operationType?: string,
    userId?: string,
    status?: string
  ): Promise<any> {
    const params = new URLSearchParams();
    if (resourceType) params.append('resourceType', resourceType);
    if (resourceId) params.append('resourceId', resourceId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (page) params.append('page', page.toString());
    if (size) params.append('size', size.toString());
    if (search) params.append('search', search);
    if (operationType) params.append('operationType', operationType);
    if (userId) params.append('userId', userId);
    if (status) params.append('status', status);
    
    const queryString = params.toString();
    const url = `/api/mtls/audit${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  // Enhanced mTLS Certificate Report API
  static async getMtlsCertificateReport(
    organizationId?: number,
    status?: string,
    expiryDays?: number,
    page?: number,
    size?: number,
    search?: string
  ): Promise<any> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId.toString());
    if (status) params.append('status', status);
    if (expiryDays) params.append('expiryDays', expiryDays.toString());
    if (page) params.append('page', page.toString());
    if (size) params.append('size', size.toString());
    if (search) params.append('search', search);
    
    const queryString = params.toString();
    const url = `/api/mtls/reports/certificates${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  // mTLS Report Export API
  static async exportMtlsCertificateReport(
    format: 'csv' | 'xlsx' | 'pdf',
    organizationId?: number,
    status?: string,
    expiryDays?: number,
    search?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    if (organizationId) params.append('organizationId', organizationId.toString());
    if (status) params.append('status', status);
    if (expiryDays) params.append('expiryDays', expiryDays.toString());
    if (search) params.append('search', search);
    
    const queryString = params.toString();
    const url = `/api/mtls/reports/certificates/export${queryString ? `?${queryString}` : ''}`;
    
    // Get auth token for file download
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return response.blob();
  }

  static async exportMtlsDeploymentReport(
    format: 'csv' | 'xlsx' | 'pdf',
    environmentId?: number,
    startDate?: string,
    endDate?: string,
    search?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    if (environmentId) params.append('environmentId', environmentId.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (search) params.append('search', search);
    
    const queryString = params.toString();
    const url = `/api/mtls/reports/deployments/export${queryString ? `?${queryString}` : ''}`;
    
    // Get auth token for file download
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return response.blob();
  }

  static async exportMtlsAuditTrail(
    format: 'csv' | 'xlsx' | 'pdf',
    resourceType?: string,
    startDate?: string,
    endDate?: string,
    search?: string,
    operationType?: string,
    userId?: string,
    status?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    if (resourceType) params.append('resourceType', resourceType);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (search) params.append('search', search);
    if (operationType) params.append('operationType', operationType);
    if (userId) params.append('userId', userId);
    if (status) params.append('status', status);
    
    const queryString = params.toString();
    const url = `/api/mtls/audit/export${queryString ? `?${queryString}` : ''}`;
    
    // Get auth token for file download
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return response.blob();
  }

  static async exportMtlsReport(
    reportType: 'certificates' | 'deployments' | 'audit',
    format: 'csv' | 'pdf',
    filters?: Record<string, any>
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const queryString = params.toString();
    const url = `/api/mtls/reports/${reportType}/export${queryString ? `?${queryString}` : ''}`;
    
    // Get auth token for file download
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'platform-ui';

    const response = await fetch(`${MTLS_API_BASE_URL}${url}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'client_id': clientId,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    return response.blob();
  }

  // mTLS Trust Store Management API
  static async downloadTrustStore(environmentId: number): Promise<Blob> {
    // Get auth token for file download
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || 'platform-ui';

    const response = await fetch(`${MTLS_API_BASE_URL}/api/mtls/environments/${environmentId}/truststore/download`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'client_id': clientId,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    return response.blob();
  }

  // mTLS Bulk Operations API
  static async bulkAssignCertificates(assignments: Array<{
    certificateId: number;
    environmentId: number;
  }>): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/assignments/bulk', {
      method: 'POST',
      body: JSON.stringify({ assignments }),
    });
  }

  static async bulkUnassignCertificates(assignments: Array<{
    certificateId: number;
    environmentId: number;
  }>): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/assignments/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ assignments }),
    });
  }

  // mTLS Environment Types API
  static async listMtlsEnvironmentTypes(): Promise<any> {
    return this.fetchWithAuthMtls('/api/mtls/environment-types');
  }

  // mTLS Certificate Expiry Notifications API
  static async getMtlsExpiryNotifications(days?: number): Promise<any> {
    const params = new URLSearchParams();
    if (days) params.append('days', days.toString());
    
    const queryString = params.toString();
    const url = `/api/mtls/certificates/expiry${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthMtls(url);
  }

  static async updateMtlsNotificationSettings(
    organizationId: number,
    settings: {
      notificationEmail?: string;
      notificationContactName?: string;
      expiryWarningDays?: number;
    }
  ): Promise<any> {
    return this.fetchWithAuthMtls(`/api/mtls/organizations/${organizationId}/notifications`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // IAM M2M Management API
  static async listIamUserPools(): Promise<any> {
    return this.fetchWithAuthIamM2m('/api/iam/userPools');
  }

  static async listIamUsers(userPoolId: string, page?: number, limit?: number, filter?: string): Promise<any> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (filter) params.append('filter', filter);
    
    const queryString = params.toString();
    const url = `/api/iam/users/${userPoolId}${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthIamM2m(url);
  }

  static async createIamUser(userPoolId: string, request: any): Promise<any> {
    return this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/user`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async getIamUser(userPoolId: string, username: string): Promise<any> {
    return this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/user/${username}`);
  }

  static async updateIamUser(userPoolId: string, username: string, request: any): Promise<any> {
    return this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/user/${username}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  static async deleteIamUser(userPoolId: string, username: string): Promise<void> {
    await this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/user/${username}`, {
      method: 'DELETE',
    });
  }

  static async listIamGroups(userPoolId: string, page?: number, limit?: number, filter?: string): Promise<any> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (filter) params.append('filter', filter);
    
    const queryString = params.toString();
    const url = `/api/iam/groups/${userPoolId}${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthIamM2m(url);
  }

  static async createIamGroup(userPoolId: string, request: any): Promise<any> {
    return this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/group`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async deleteIamGroup(userPoolId: string, groupName: string): Promise<void> {
    await this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/group/${groupName}`, {
      method: 'DELETE',
    });
  }

  static async listIamUsersInGroup(userPoolId: string, groupName: string, page?: number, limit?: number): Promise<any> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    const url = `/api/iam/userPools/${encodeURIComponent(userPoolId)}/group/${encodeURIComponent(groupName)}/users${queryString ? `?${queryString}` : ''}`;
    
    console.log('Making API call to:', url);
    return this.fetchWithAuthIamM2m(url);
  }

  static async assignIamUserToGroup(userPoolId: string, username: string, groupName: string): Promise<void> {
    await this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/user/${username}/group`, {
      method: 'POST',
      body: JSON.stringify({ groupName }),
    });
  }

  static async removeIamUserFromGroup(userPoolId: string, username: string, groupName: string): Promise<void> {
    await this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/user/${username}/group/${groupName}`, {
      method: 'DELETE',
    });
  }

  static async listIamAppClients(userPoolId: string, page?: number, limit?: number, filter?: string): Promise<any> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (filter) params.append('filter', filter);
    
    const queryString = params.toString();
    const url = `/api/iam/appClients/${userPoolId}${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthIamM2m(url);
  }

  static async createIamAppClient(userPoolId: string, request: any): Promise<any> {
    return this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/appClient`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  static async getIamAppClient(userPoolId: string, clientId: string, includeSecret: boolean = false): Promise<any> {
    const params = new URLSearchParams();
    if (includeSecret) params.append('includeSecret', 'true');
    
    const queryString = params.toString();
    const url = `/api/iam/userPools/${userPoolId}/appClient/${clientId}${queryString ? `?${queryString}` : ''}`;
    
    return this.fetchWithAuthIamM2m(url);
  }

  static async updateIamAppClient(userPoolId: string, clientId: string, request: any): Promise<any> {
    return this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/appClient/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  static async deleteIamAppClient(userPoolId: string, clientId: string): Promise<void> {
    await this.fetchWithAuthIamM2m(`/api/iam/userPools/${userPoolId}/appClient/${clientId}`, {
      method: 'DELETE',
    });
  }
}

// Export the appropriate API service based on environment
const selectedService = USE_LOCAL_FS 
  ? 'LocalFsApiService'
  : (USE_MOCK ? 'MockApiService' : 'RealApiService');

console.log('Selected API Service:', selectedService);

export const ApiService = USE_LOCAL_FS 
  ? LocalFsApiService 
  : (USE_MOCK ? MockApiService : RealApiService);
